import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import {
    Entitlements,
    FeatureKey,
    LimitKey,
    OperatingRule,
} from 'app/core/entitlements/entitlements.types';
import { findPlan, PLANS } from 'app/core/entitlements/plans.config';
import { environment } from 'environments/environment';
import { map, Observable, tap } from 'rxjs';

/**
 * Resolves what the signed-in tenant is allowed to see.
 *
 * The frontend decides what to SHOW; the API decides what to ALLOW. Anything
 * gated here must also be enforced server-side — a user can edit their own
 * browser state, so a check that only lives here is decoration.
 *
 * Source of truth is now `GET /api/v1/entitlements`, which resolves the plan
 * from the tenant row and counts what is already used against it. The local
 * `plans.config.ts` remains as the fallback that renders the tier screens
 * before that call returns, and as the dev override.
 */

/** The payload as the API writes it: snake case, features as a list. */
interface EntitlementsResponse {
    plan_code: string;
    effective_plan_code: string;
    plan_name: string;
    operating_rule: OperatingRule;
    limits: Record<string, number | null>;
    usage: Record<string, number>;
    features: string[];
    upgrade_to: string | null;
    next_plan: { code: string; name: string; limits: Record<string, number | null> } | null;
}

/** trips_per_month on the wire, tripsPerMonth in here. */
const LIMIT_KEYS: Record<string, LimitKey> = {
    aircraft: 'aircraft',
    pilots: 'pilots',
    crew: 'crew',
    passengers: 'passengers',
    trips_per_month: 'tripsPerMonth',
    legs_per_month: 'legsPerMonth',
    expenses_per_month: 'expensesPerMonth',
    reports_per_month: 'reportsPerMonth',
    requests_per_month: 'requestsPerMonth',
    ai_messages_per_month: 'aiMessagesPerMonth',
    storage_mb: 'storageMb',
};

function toLimits(source: Record<string, number | null> = {}): Partial<Record<LimitKey, number | null>> {
    const out: Partial<Record<LimitKey, number | null>> = {};

    Object.entries(source).forEach(([key, value]) => {
        const mapped = LIMIT_KEYS[key];
        if (mapped) {
            out[mapped] = value;
        }
    });

    return out;
}

/** Until a plan is known, nothing is restricted — so the UI never flickers
 *  a lock on for users whose plan simply has not loaded yet. */
const UNRESTRICTED: Entitlements = {
    planCode: 'unknown',
    planName: '',
    operatingRule: 'part91',
    features: new Proxy({}, { get: () => true }) as Entitlements['features'],
    limits: {},
};

const DEV_PLAN_OVERRIDE_KEY = 'altura.devPlanOverride';

/** The last plan this device saw, so the menu opens right while the server is asked again. */
const LAST_PLAN_KEY = 'altura.lastPlan';

@Injectable({ providedIn: 'root' })
export class EntitlementsService {
    readonly #http = inject(HttpClient);

    private readonly _entitlements = signal<Entitlements>(UNRESTRICTED);

    /** What the next rung up allows, as the API reports it. */
    private readonly _nextPlan = signal<{
        code: string; name: string; limits: Partial<Record<LimitKey, number | null>>;
    } | null>(null);

    readonly nextPlanFromApi = this._nextPlan.asReadonly();

    readonly entitlements = this._entitlements.asReadonly();
    readonly planName = computed(() => this._entitlements().planName);
    readonly operatingRule = computed(() => this._entitlements().operatingRule);

    /** True when the last plan seen on this device was restored, so nothing has to wait for the server. */
    readonly restoredFromDevice: boolean;

    constructor() {
        this.restoredFromDevice = this.restoreLastPlan();
        this.applyDevOverride();
    }

    private restoreLastPlan(): boolean {
        try {
            const saved = JSON.parse(localStorage.getItem(LAST_PLAN_KEY) ?? 'null') as Entitlements | null;

            if (saved?.planCode && saved.features) {
                this._entitlements.set(saved);

                return true;
            }
        } catch {
            // Unreadable or blocked storage: the plan is loaded from the server as before.
        }

        return false;
    }

    private rememberPlan(entitlements: Entitlements): void {
        try {
            localStorage.setItem(LAST_PLAN_KEY, JSON.stringify(entitlements));
        } catch {
            // Not remembered; the next open asks the server first.
        }
    }

    /**
     * Adopt the entitlements the API reported.
     *
     * Call this with the `entitlements` object from `GET /users/me`. Until that
     * field exists, `setPlan()` fills in from the local catalogue.
     */
    loadFrom(entitlements: Entitlements | null | undefined): void {
        if (entitlements) {
            this._entitlements.set(entitlements);
        }
    }

    /**
     * Ask the API what this workspace is allowed to do, and adopt the answer.
     *
     * The server owns both the ceiling and the tally. Counting rows in here
     * would eventually disagree with the save that actually gets refused.
     */
    load(): Observable<Entitlements> {
        return this.#http
            .get<EntitlementsResponse>(`${environment.apiUrl}/api/v1/entitlements`, {
                headers: { 'X-Skip-Loading-Bar': 'true' },
            })
            .pipe(
                map((response) => {
                    const features: Partial<Record<FeatureKey, boolean>> = {};
                    (response.features ?? []).forEach((f) => (features[f as FeatureKey] = true));

                    this._nextPlan.set(
                        response.next_plan
                            ? {
                                  code: response.next_plan.code,
                                  name: response.next_plan.name,
                                  limits: toLimits(response.next_plan.limits),
                              }
                            : null
                    );

                    return {
                        planCode: response.effective_plan_code ?? response.plan_code,
                        planName: response.plan_name,
                        operatingRule: response.operating_rule,
                        features,
                        limits: toLimits(response.limits),
                        usage: toLimits(response.usage as Record<string, number>) as Partial<
                            Record<LimitKey, number>
                        >,
                    } satisfies Entitlements;
                }),
                tap((entitlements) => {
                    this._entitlements.set(entitlements);
                    this.rememberPlan(entitlements);
                })
            );
    }

    /** How many are already spoken for, as the server counts them. */
    usedOf(limit: LimitKey): number {
        return this._entitlements().usage?.[limit] ?? 0;
    }

    /** Is there room for one more? */
    hasRoomFor(limit: LimitKey): boolean {
        return this.withinLimit(limit, this.usedOf(limit));
    }

    /** Adopt a plan from the local catalogue, by code. */
    setPlan(planCode: string): boolean {
        const plan = findPlan(planCode);
        if (!plan) {
            return false;
        }
        const { priceCents, upgradeTo, ...entitlements } = plan;
        this._entitlements.set(entitlements);
        return true;
    }

    /** Is this feature switched on for the current plan? */
    allows(feature: FeatureKey): boolean {
        return this._entitlements().features[feature] === true;
    }

    /** Would one more of these stay inside the plan's ceiling? */
    withinLimit(limit: LimitKey, current: number): boolean {
        const max = this._entitlements().limits[limit];
        // Absent or null both mean "no ceiling" — absent because the backend
        // may simply not report a limit it does not enforce.
        return max === null || max === undefined || current < max;
    }

    /** The ceiling itself, for messages like "1 of 3 aircraft". */
    limitFor(limit: LimitKey): number | null {
        return this._entitlements().limits[limit] ?? null;
    }

    /**
     * What the next rung up allows, as a few plain lines.
     *
     * Built from the plan's own ceilings rather than written by hand, so the
     * offer cannot drift from what is actually sold. Four at most: past that
     * it stops being an answer and becomes a pricing page.
     */
    nextPlanHighlights(labels: Partial<Record<LimitKey, string>>, unlimited: string): string[] {
        const limits = this._nextPlan()?.limits;

        if (!limits) {
            return [];
        }

        return (Object.keys(labels) as LimitKey[])
            .filter((key) => key in limits)
            .map((key) => {
                const value = limits[key];

                return value === null || value === undefined
                    ? `${unlimited} ${labels[key]}`
                    : `${value} ${labels[key]}`;
            })
            .slice(0, 4);
    }

    /** The cheapest plan on the current ladder that switches this feature on. */
    cheapestPlanWith(feature: FeatureKey) {
        return PLANS[this._entitlements().operatingRule].find(
            (p) => p.features[feature] === true
        );
    }

    /** Whether this workspace is on the free rung of its ladder. */
    onFreePlan(): boolean {
        return (findPlan(this._entitlements().planCode)?.priceCents ?? 0) === 0;
    }

    /** The next rung up, if there is one. */
    nextPlan() {
        const current = findPlan(this._entitlements().planCode);
        return current?.upgradeTo ? findPlan(current.upgradeTo) : undefined;
    }

    plansFor(rule?: OperatingRule) {
        return PLANS[rule ?? this._entitlements().operatingRule];
    }

    /**
     * Lets a plan be forced from the browser console on non-production builds,
     * so the tiers can be reviewed on a deployed preview before the backend
     * serves them:
     *
     *   localStorage.setItem('altura.devPlanOverride', 'p91_free'); location.reload()
     *
     * Ignored in production — and harmless anywhere, because the API enforces
     * the real limits regardless of what the browser believes.
     */
    private applyDevOverride(): void {
        if (environment.production) {
            return;
        }
        try {
            const planCode = localStorage.getItem(DEV_PLAN_OVERRIDE_KEY);
            if (planCode) {
                this.setPlan(planCode);
            }
        } catch {
            // Storage can be unavailable (private window, blocked cookies).
            // The default entitlements are already correct, so there is
            // nothing to recover from.
        }
    }
}
